<script setup>
import { computed, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import AgentFormModal from './AgentFormModal.vue';
import AgentsAPI from 'dashboard/api/agents';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  thumbnail: {
    type: String,
    default: '',
  },
  displayName: {
    type: String,
    default: '',
  },
  phoneNumber: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  teams: {
    type: Array,
    default: () => [],
  },
  sessions: {
    type: Array,
    default: () => [],
  },
  activeSessionsCount: {
    type: Number,
    default: 0,
  },
  currentSignInAt: {
    type: String,
    default: '',
  },
  currentSignInIp: {
    type: String,
    default: '',
  },
  lastSignInAt: {
    type: String,
    default: '',
  },
  lastSignInIp: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('agents/getUIFlags');
const isSubmitting = computed(() => uiFlags.value.isUpdating);
const emailVerified = computed(
  () =>
    store.getters['agents/getAgents'].find(agent => agent.id === props.id)
      ?.email_verified ?? false
);
const verificationSending = ref(false);
const verificationSent = ref(false);

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} — ${props.name}`
);

// Espelha exatamente o que o modal de criacao grava: os mesmos campos, com os
// valores que ja estao salvos no usuario. E um snapshot do momento em que o
// modal abriu, nao um computed, para o formulario nao se redesenhar sozinho.
const initialValue = {
  name: props.name,
  email: props.email,
  phone: props.phoneNumber,
  displayName: props.displayName,
  availability: props.availability,
  roleId: props.customRoleId || props.type || 'agent',
  nativeRole: props.type || 'agent',
  teamRoles: Object.fromEntries(
    props.teams.map(team => [team.id, team.role || 'user'])
  ),
};

const formatDateTime = value => {
  if (!value) return t('AGENT_MGMT.EDIT.DEVICES.NEVER');

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
};

const deviceName = session =>
  session.device_name || t('AGENT_MGMT.EDIT.DEVICES.UNKNOWN_DEVICE');

const editAgent = async form => {
  try {
    await store.dispatch('agents/update', {
      id: props.id,
      name: form.name,
      display_name: form.displayName,
      phone_number: form.phone,
      availability: form.availability,
      role: form.role,
      custom_role_id: form.customRoleId,
      team_assignments: form.teamAssignments,
    });
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  }
};

const requestEmailVerification = async () => {
  verificationSending.value = true;
  try {
    await AgentsAPI.requestEmailVerification(props.id);
    verificationSent.value = true;
  } catch (error) {
    useAlert(t('REGISTER.VERIFY_EMAIL.RESEND_ERROR'));
  } finally {
    verificationSending.value = false;
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword({ email: props.email });
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <AgentFormModal
    :title="pageTitle"
    :description="$t('AGENT_MGMT.EDIT.DESC')"
    :submit-label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
    submit-icon="i-lucide-check"
    :is-submitting="isSubmitting"
    :initial-value="initialValue"
    :avatar-url="thumbnail"
    :email-editable="false"
    :email-verified="emailVerified"
    mobile-layout
    unified-mobile-actions
    @close="emit('close')"
    @submit="editAgent"
  >
    <template #email-verification>
      <Button
        v-if="!verificationSent"
        class="mt-2"
        type="button"
        color="slate"
        variant="outline"
        size="sm"
        :is-loading="verificationSending"
        :disabled="verificationSending"
        :label="$t('APP_GLOBAL.RESEND_VERIFICATION_MAIL')"
        @click="requestEmailVerification"
      />
      <span v-else class="mt-2 block">{{
        $t('REGISTER.VERIFY_EMAIL.RESEND_SUCCESS')
      }}</span>
    </template>
    <template #sections>
      <section
        class="edit-agent__devices flex flex-col gap-4 border-t border-n-weak px-5 py-6 sm:px-7"
      >
        <div class="edit-agent__devices-heading">
          <h3
            class="edit-agent__devices-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ $t('AGENT_MGMT.EDIT.DEVICES.TITLE') }}
          </h3>
          <p
            class="edit-agent__devices-description mb-0 mt-1 text-sm leading-5 text-n-slate-11"
          >
            {{
              $t('AGENT_MGMT.EDIT.DEVICES.DESCRIPTION', {
                count: activeSessionsCount,
              })
            }}
          </p>
        </div>

        <div class="edit-agent__sign-ins grid gap-3 sm:grid-cols-2">
          <div
            class="edit-agent__sign-in edit-agent__sign-in--current flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3"
          >
            <span
              class="edit-agent__sign-in-icon i-lucide-log-in mt-0.5 size-4 flex-shrink-0 text-n-brand"
            />
            <div class="edit-agent__sign-in-text min-w-0">
              <span
                class="edit-agent__sign-in-label block text-xs text-n-slate-10"
              >
                {{ $t('AGENT_MGMT.EDIT.DEVICES.CURRENT_SIGN_IN') }}
              </span>
              <span
                class="edit-agent__sign-in-value mt-0.5 block text-sm text-n-slate-12"
              >
                {{ formatDateTime(currentSignInAt) }}
              </span>
              <span
                class="edit-agent__sign-in-ip block text-xs text-n-slate-10"
              >
                {{ currentSignInIp || $t('AGENT_MGMT.EDIT.DEVICES.NO_IP') }}
              </span>
            </div>
          </div>
          <div
            class="edit-agent__sign-in edit-agent__sign-in--last flex items-start gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3"
          >
            <span
              class="edit-agent__sign-in-icon i-lucide-history mt-0.5 size-4 flex-shrink-0 text-n-slate-11"
            />
            <div class="edit-agent__sign-in-text min-w-0">
              <span
                class="edit-agent__sign-in-label block text-xs text-n-slate-10"
              >
                {{ $t('AGENT_MGMT.EDIT.DEVICES.LAST_SIGN_IN') }}
              </span>
              <span
                class="edit-agent__sign-in-value mt-0.5 block text-sm text-n-slate-12"
              >
                {{ formatDateTime(lastSignInAt) }}
              </span>
              <span
                class="edit-agent__sign-in-ip block text-xs text-n-slate-10"
              >
                {{ lastSignInIp || $t('AGENT_MGMT.EDIT.DEVICES.NO_IP') }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="edit-agent__sessions overflow-hidden rounded-lg border border-n-weak"
        >
          <span
            v-if="!sessions.length"
            class="edit-agent__sessions-empty block px-4 py-3 text-sm text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.EDIT.DEVICES.EMPTY') }}
          </span>
          <div
            v-for="session in sessions"
            :key="session.id"
            class="edit-agent__session flex items-center gap-3 border-b border-n-weak px-4 py-3 last:border-b-0"
          >
            <span
              class="edit-agent__session-icon i-lucide-monitor-smartphone size-4 flex-shrink-0 text-n-slate-11"
            />
            <div class="edit-agent__session-text min-w-0">
              <span
                class="edit-agent__session-name block truncate text-sm text-n-slate-12"
              >
                {{ deviceName(session) }}
              </span>
              <span
                class="edit-agent__session-expiry block text-xs text-n-slate-10"
              >
                {{
                  $t('AGENT_MGMT.EDIT.DEVICES.EXPIRES_AT', {
                    date: formatDateTime(session.expiry),
                  })
                }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </template>

    <template #footer-start>
      <Button
        v-if="provider !== 'saml'"
        ghost
        slate
        type="button"
        icon="i-lucide-lock-keyhole"
        class="edit-agent__reset-password max-sm:bg-n-ruby-9/10 max-sm:text-n-ruby-11 max-sm:outline-n-ruby-8 max-sm:hover:bg-n-ruby-9/20 max-sm:focus-visible:bg-n-ruby-9/20"
        :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
        @click="resetPassword"
      />
    </template>
  </AgentFormModal>
</template>
